import { useCallback, useEffect, useRef, useState } from "react";
import { adminStorage } from "../../features/admin/service";
import { apiClient } from "../../lib/http/apiClient";
import { parseApiError } from "../../lib/http/apiErrors";

// Admin-side media upload for a partner's public channel gallery (the
// "Showcase" grid on the partner profile page). Admin uploads the file
// directly (multipart, no presigned-URL round trip — admin sessions carry an
// admin token, not the seller/partner token that flow requires) via
// POST /api/admin/partners/{owner}/showcase, which both stores the file in S3
// and registers it on the partner doc in one call.

interface ShowcaseItem {
  media_id: string;
  type: "video" | "image" | string;
  url?: string | null;
  caption?: string | null;
  order: number;
}

function adminHeaders() {
  const token = adminStorage.getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export default function PartnerShowcaseEditor({ ownerId }: { ownerId: string }) {
  const [items, setItems] = useState<ShowcaseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [caption, setCaption] = useState("");
  const [err, setErr] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCaption, setEditCaption] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<{ items: ShowcaseItem[] }>(
        `/api/admin/partners/${ownerId}/showcase`,
        { headers: adminHeaders() },
      );
      setItems(data.items || []);
    } catch (e) {
      setErr(parseApiError(e).message);
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  useEffect(() => { void load(); }, [load]);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setUploading(true);
    setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (caption.trim()) fd.append("caption", caption.trim());
      fd.append("order", String(items.length));
      await apiClient.post(`/api/admin/partners/${ownerId}/showcase`, fd, {
        headers: { ...adminHeaders(), "Content-Type": "multipart/form-data" },
      });
      setCaption("");
      await load();
    } catch (e) {
      setErr(parseApiError(e).message || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const startEdit = (item: ShowcaseItem) => {
    setEditingId(item.media_id);
    setEditCaption(item.caption ?? "");
  };

  const saveEdit = async (mediaId: string) => {
    setSavingEdit(true);
    setErr("");
    try {
      await apiClient.patch(
        `/api/admin/partners/${ownerId}/showcase/${mediaId}`,
        { caption: editCaption.trim() || null },
        { headers: adminHeaders() },
      );
      setItems((prev) => prev.map((i) => (i.media_id === mediaId ? { ...i, caption: editCaption.trim() || null } : i)));
      setEditingId(null);
    } catch (e) {
      setErr(parseApiError(e).message || "Save failed");
    } finally {
      setSavingEdit(false);
    }
  };

  const remove = async (mediaId: string) => {
    setDeletingId(mediaId);
    setErr("");
    try {
      await apiClient.delete(`/api/admin/partners/${ownerId}/showcase/${mediaId}`, {
        headers: adminHeaders(),
      });
      setItems((prev) => prev.filter((i) => i.media_id !== mediaId));
    } catch (e) {
      setErr(parseApiError(e).message || "Delete failed");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-6">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-sm font-semibold text-white">Showcase media</h2>
        <span className="text-[11px] text-gray-500">{items.length} item{items.length === 1 ? "" : "s"}</span>
      </div>
      <p className="text-xs text-gray-500 mb-4">Photos and videos shown on the partner's public channel page.</p>

      {err && <div className="mb-4 px-3 py-2 bg-red-950/80 border border-red-800/60 rounded-lg text-red-200 text-xs">{err}</div>}

      <div className="flex flex-wrap items-center gap-2 mb-5">
        <input
          type="text"
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Caption (optional)"
          className="flex-1 min-w-[160px] px-2.5 py-1.5 bg-gray-800 border border-gray-700 rounded-lg text-white text-xs placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/40"
        />
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          className="hidden"
          onChange={(e) => void pick(e.target.files?.[0])}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="px-4 py-1.5 bg-indigo-600 hover:bg-indigo-500 rounded-lg text-xs font-medium text-white disabled:opacity-50"
        >
          {uploading ? "Uploading…" : "Upload photo / video"}
        </button>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => <div key={i} className="aspect-square bg-gray-800/60 rounded-lg animate-pulse" />)}
        </div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-gray-800 rounded-xl px-6 py-8 text-center text-xs text-gray-600">
          No showcase media yet — upload the first photo or video above.
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {items.map((item) => (
            <div key={item.media_id} className="group relative aspect-square rounded-lg overflow-hidden border border-gray-800 bg-gray-800">
              {item.type === "video" ? (
                <video src={item.url ?? undefined} className="h-full w-full object-cover" muted />
              ) : (
                <img src={item.url ?? undefined} alt={item.caption ?? ""} className="h-full w-full object-cover" />
              )}
              <span className="absolute left-1.5 top-1.5 px-1.5 py-0.5 rounded bg-black/60 text-[10px] font-medium text-white capitalize">
                {item.type}
              </span>
              <div className="absolute right-1.5 top-1.5 flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                <button
                  type="button"
                  onClick={() => startEdit(item)}
                  className="flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white hover:bg-indigo-600"
                  aria-label="Edit caption"
                >
                  ✎
                </button>
                <button
                  type="button"
                  onClick={() => void remove(item.media_id)}
                  disabled={deletingId === item.media_id}
                  className="flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white hover:bg-red-600 disabled:opacity-50"
                  aria-label="Remove"
                >
                  {deletingId === item.media_id ? "…" : "×"}
                </button>
              </div>
              {editingId === item.media_id ? (
                <div className="absolute inset-x-0 bottom-0 flex gap-1 bg-black/80 p-1.5">
                  <input
                    autoFocus
                    value={editCaption}
                    onChange={(e) => setEditCaption(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && void saveEdit(item.media_id)}
                    placeholder="Caption"
                    className="min-w-0 flex-1 rounded bg-gray-800 px-1.5 py-1 text-[10px] text-white placeholder-gray-500 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => void saveEdit(item.media_id)}
                    disabled={savingEdit}
                    className="shrink-0 rounded bg-indigo-600 px-1.5 text-[10px] text-white hover:bg-indigo-500 disabled:opacity-50"
                  >
                    {savingEdit ? "…" : "Save"}
                  </button>
                </div>
              ) : (
                item.caption && (
                  <button
                    type="button"
                    onClick={() => startEdit(item)}
                    className="absolute inset-x-0 bottom-0 truncate bg-black/60 px-1.5 py-1 text-left text-[10px] text-white hover:bg-black/75"
                  >
                    {item.caption}
                  </button>
                )
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
